import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const COUNTIES = ["New Castle", "Kent", "Sussex"];

const FIELDS = [
  ["base_fee", "Service fee — flat (out-of-state clients, or fallback)", "65.00"],
  ["same_address_fee", "2nd+ servee, same address", "45.00"],
  ["rush_fee", "Rush add-on", "0.00"],
  ["same_day_fee", "Same-day add-on", "0.00"],
  ["included_pages", "Pages included", "25"],
  ["page_fee", "Per page after that", "0.20"],
  ["check_fee", "Check issuance (when we advance a court fee)", "0.00"],
];

/** Billing rate card editor for a company. value = { base_fee, ..., fee_overrides: { [fee_id]: price } }. */
export function RateCardFields({ value, onChange, billingEmail, onBillingEmail, readOnly }) {
  const v = value || {};
  const set = (k) => (e) => onChange({ ...v, [k]: e.target.value });
  const [fees, setFees] = useState([]);
  useEffect(() => { api.get("/fees").then((r) => setFees(r.data.filter((f) => f.active))).catch(() => {}); }, []);
  const ov = v.fee_overrides || {};
  const setOv = (id) => (e) => onChange({ ...v, fee_overrides: { ...ov, [id]: e.target.value } });
  const cf = v.county_fees || {};
  const setCf = (c) => (e) => onChange({ ...v, county_fees: { ...cf, [c]: e.target.value } });
  return (
    <div className="rounded border border-slate-200 bg-slate-50 p-3 space-y-3" data-testid="rate-card">
      <div className="text-xs text-slate-600">Prices used on the Not-Invoiced remittance sheet. Leave the service fee blank if you bill this company by hand.</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {FIELDS.map(([k, label, ph]) => (
          <div key={k} className="space-y-1">
            <Label className="sm-label">{label}</Label>
            <div className="relative">
              {k !== "included_pages" && <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>}
              <Input className={k !== "included_pages" ? "pl-6 h-9" : "h-9"} type="number" step={k === "included_pages" ? "1" : "0.01"} min="0" placeholder={ph} disabled={readOnly}
                value={v[k] ?? ""} onChange={set(k)} data-testid={`rate-${k}`} />
            </div>
          </div>
        ))}
      </div>
      <div className="space-y-2" data-testid="rate-card-counties">
        <div className="text-xs text-slate-600"><b>Service fee by county</b> — for local clients priced by where the servee is. Leave a county blank to use the flat fee above. Jobs in other states always use the flat fee.</div>
        <div className="grid grid-cols-3 gap-3">
          {COUNTIES.map((c) => (
            <div key={c} className="space-y-1">
              <Label className="sm-label">{c} County</Label>
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                <Input className={`pl-6 h-9 ${cf[c] ? "border-sky-400 bg-sky-50" : ""}`} type="number" step="0.01" min="0" placeholder={v.base_fee ? Number(v.base_fee).toFixed(2) : "flat fee"} disabled={readOnly} value={cf[c] ?? ""} onChange={setCf(c)} data-testid={`rate-county-${c.replace(" ", "-").toLowerCase()}`} />
              </div>
            </div>
          ))}
        </div>
      </div>
      {fees.length > 0 && (
        <div className="space-y-2" data-testid="rate-card-fees">
          <div className="text-xs text-slate-600"><b>Additional fees</b> — this company's price for each catalog fee. Blank = the default shown. <span className="text-slate-400">(Manage the catalog under Settings → Fee catalog.)</span></div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {fees.map((f) => (
              <div key={f.id} className="space-y-1">
                <Label className="sm-label">{f.name}{f.unit === "per_unit" && f.unit_label ? <span className="font-normal text-slate-400"> / {f.unit_label}</span> : null}</Label>
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                  <Input className={`pl-6 h-9 ${ov[f.id] ? "border-sky-400 bg-sky-50" : ""}`} type="number" step="0.01" min="0" placeholder={Number(f.default_price).toFixed(2)} disabled={readOnly} value={ov[f.id] ?? ""} onChange={setOv(f.id)} data-testid={`rate-fee-${f.id}`} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="space-y-1">
        <Label className="sm-label">Billing / remittance email <span className="font-normal text-slate-400">(defaults to the ★ primary contact)</span></Label>
        <Input className="h-9" type="email" placeholder="ap@client.com" disabled={readOnly} value={billingEmail || ""} onChange={(e) => onBillingEmail(e.target.value)} data-testid="billing-email" />
      </div>
    </div>
  );
}
